import React, { useState } from 'react';
import { Book, MessageCircle, FileQuestion, Mail, ExternalLink } from 'lucide-react';
import { FAQSection } from './FAQSection';
import { HelpCenter } from './HelpCenter';

export const HelpView: React.FC = () => {
  const [showHelpCenter, setShowHelpCenter] = useState(false);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Help & Support</h1>
        <p className="text-slate-600">
          Get help with VendorBoom and find answers to common questions
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <button
          onClick={() => setShowHelpCenter(true)}
          className="bg-white border border-slate-200 rounded-xl p-6 hover:border-slate-300 hover:shadow-sm transition-all text-left group"
        >
          <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-slate-200 transition-colors">
            <Book className="w-6 h-6 text-slate-700" />
          </div>
          <h3 className="font-semibold text-slate-900 mb-2">Help Articles</h3>
          <p className="text-sm text-slate-600">
            Browse our comprehensive knowledge base
          </p>
        </button>

        <a
          href="mailto:support@vendorboom.com"
          className="bg-white border border-slate-200 rounded-xl p-6 hover:border-slate-300 hover:shadow-sm transition-all text-left group"
        >
          <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-slate-200 transition-colors">
            <Mail className="w-6 h-6 text-slate-700" />
          </div>
          <h3 className="font-semibold text-slate-900 mb-2">Email Support</h3>
          <p className="text-sm text-slate-600">
            Get help via email within 24 hours
          </p>
        </a>

        <button className="bg-white border border-slate-200 rounded-xl p-6 hover:border-slate-300 hover:shadow-sm transition-all text-left group">
          <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-slate-200 transition-colors">
            <MessageCircle className="w-6 h-6 text-slate-700" />
          </div>
          <h3 className="font-semibold text-slate-900 mb-2">Live Chat</h3>
          <p className="text-sm text-slate-600">
            Chat with our support team in real-time
          </p>
          <span className="inline-block mt-2 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
            Coming Soon
          </span>
        </button>

        <a
          href="#"
          className="bg-white border border-slate-200 rounded-xl p-6 hover:border-slate-300 hover:shadow-sm transition-all text-left group"
        >
          <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-slate-200 transition-colors">
            <FileQuestion className="w-6 h-6 text-slate-700" />
          </div>
          <h3 className="font-semibold text-slate-900 mb-2">Video Tutorials</h3>
          <p className="text-sm text-slate-600">
            Watch step-by-step video guides
          </p>
          <span className="inline-block mt-2 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
            Coming Soon
          </span>
        </a>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Quick Links</h2>
            <p className="text-slate-600">Common resources and helpful links</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <a
            href="#"
            className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors group"
          >
            <span className="font-medium text-slate-900">Getting Started Guide</span>
            <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-slate-600" />
          </a>

          <a
            href="#"
            className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors group"
          >
            <span className="font-medium text-slate-900">Terms of Service</span>
            <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-slate-600" />
          </a>

          <a
            href="#"
            className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors group"
          >
            <span className="font-medium text-slate-900">Privacy Policy</span>
            <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-slate-600" />
          </a>

          <a
            href="#"
            className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors group"
          >
            <span className="font-medium text-slate-900">Community Guidelines</span>
            <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-slate-600" />
          </a>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <FAQSection />
      </div>

      <div className="bg-slate-900 rounded-xl p-8 text-center">
        <h2 className="text-2xl font-bold text-white mb-3">Still need help?</h2>
        <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
          Our support team is here to help you succeed. Reach out anytime and we'll get back to you as soon as possible.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <a
            href="mailto:support@vendorboom.com"
            className="px-6 py-3 bg-white text-slate-900 font-medium rounded-lg hover:bg-slate-100 transition-colors"
          >
            Email Support
          </a>
          <button
            onClick={() => setShowHelpCenter(true)}
            className="px-6 py-3 border-2 border-white text-white font-medium rounded-lg hover:bg-white hover:text-slate-900 transition-colors"
          >
            Browse Help Articles
          </button>
        </div>
      </div>

      <HelpCenter isOpen={showHelpCenter} onClose={() => setShowHelpCenter(false)} />
    </div>
  );
};
